"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { apiGetQuiet, apiPost, apiPostQuiet } from "@/lib/api";

interface AuthContextValue {
  user: string | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGetQuiet<{ username: string }>("/auth/me")
      .then((res) => setUser(res.username))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const res = await apiPostQuiet<{ username: string }>("/auth/login", {
      username,
      password,
    });
    setUser(res.username);
  }, []);

  const logout = useCallback(async () => {
    await apiPost("/auth/logout", {});
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
